import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { authApi } from '../api/services';
import BrandMark from '../components/BrandMark';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// ForgotPasswordPage — themed to match LoginPage. Dark cinematic backdrop
// with copper halo + cool-blue top glow, SVG grain overlay, no card chrome,
// .rise staggered entrance and a leave-veil on successful submit.
// ---------------------------------------------------------------------------

// Grain texture — SVG feTurbulence fractalNoise overlay encoded as data-URI.
const grainSvg =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'>` +
      `<filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter>` +
      `<rect width='100%' height='100%' filter='url(#n)' opacity='1'/>` +
      `</svg>`,
  );

// Field tokens — aligned with the Login page so the auth funnel reads as one.
const FIELD_CLASS =
  'h-12 rounded-[11px] border border-[rgba(234,238,246,0.12)] bg-[rgba(234,238,246,0.04)] px-[15px] py-[13px] text-[15px] text-[#EAEEF6] placeholder:text-[rgba(234,238,246,0.30)] focus-visible:border-[#7DB1FF] focus-visible:ring-[3px] focus-visible:ring-[rgba(125,177,255,0.16)]';

// Primary submit — near-white button on dark, ink-dark label, blue-glow hover.
const PRIMARY_BUTTON =
  'h-12 w-full rounded-[11px] border-0 bg-[#EAEEF6] text-[15px] font-semibold text-[#070A12] transition-transform duration-150 hover:-translate-y-px hover:bg-[#EAEEF6] hover:shadow-[0_10px_32px_rgba(125,177,255,0.22)] disabled:opacity-60 disabled:hover:translate-y-0';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Document title parity with Login.
  useEffect(() => {
    const prev = document.title;
    document.title = 'Reset your password · Cadence';
    return () => { document.title = prev; };
  }, []);

  // Entrance — triple-redundant trigger so .rise reveals fire even if React
  // paints late or visibility flips.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const reveal = () => { if (!cancelled) setReady(true); };
    const t1 = window.setTimeout(reveal, 120);
    const t2 = window.setTimeout(reveal, 200);
    window.addEventListener('load', reveal);
    const onVis = () => { if (document.visibilityState === 'visible') reveal(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener('load', reveal);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Leave-veil — soft fade up on successful submit so the page doesn't flash.
  const [leaving, setLeaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    try {
      await authApi.forgotPassword(email.trim());
      // Veil up briefly so the success state lands softly rather than cutting.
      setLeaving(true);
      await new Promise((r) => setTimeout(r, 250));
      setSubmitted(true);
      setLeaving(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
      setLeaving(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark relative flex min-h-screen items-center justify-center overflow-hidden bg-[#070A12]">
      {/* Ambient backdrop layer (z=0): copper halo bottom-centre + cool-blue
          top-glow + top/bottom vignette. Matches LoginPage. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
        <div
          className="absolute left-1/2 bottom-[-24%] h-[78vh] w-[120vw] -translate-x-1/2 rounded-full blur-[14px]"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(255,176,122,0.14) 0%, transparent 62%)',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(120% 90% at 50% -10%, rgba(125,177,255,0.05), transparent 55%), linear-gradient(180deg, rgba(0,0,0,0.28), transparent 32%, transparent 68%, rgba(0,0,0,0.45))',
          }}
        />
      </div>

      {/* Grain texture layer (z=1). */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{
          backgroundImage: `url("${grainSvg}")`,
          backgroundSize: '160px 160px',
          opacity: 0.04,
          mixBlendMode: 'overlay',
        }}
      />

      {/* Leave-veil (z=60). */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 z-[60] transition-opacity duration-[520ms] ease-out',
          leaving ? 'opacity-100' : 'opacity-0',
        )}
        style={{ background: '#070A12' }}
      />

      {/* Form column (z=2) — no card chrome, sits on the backdrop. */}
      <div className="relative z-[2] mx-4 flex w-full max-w-[392px] flex-col p-[32px]">
        <div
          className={cn('rise mb-5', ready && 'is-revealed')}
          style={{ '--d': '120ms' } as React.CSSProperties}
        >
          <BrandMark size="md" showStratiumLine inlineStratiumTagline accent="light" />
        </div>

        <h1
          className={cn(
            'rise mb-[10px] font-serif text-[33px] font-light leading-[1.12] tracking-[-0.01em] text-white',
            ready && 'is-revealed',
          )}
          style={{ '--d': '260ms' } as React.CSSProperties}
        >
          Reset your password.
        </h1>
        <p
          className={cn(
            'rise mb-[30px] text-[15px] font-light tracking-wide text-[rgba(234,238,246,0.62)]',
            ready && 'is-revealed',
          )}
          style={{ '--d': '380ms' } as React.CSSProperties}
        >
          We'll email you a link to set a new one.
        </p>

        {error && (
          <Alert variant="error" className="mb-3 rounded-xl">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {submitted ? (
          <div
            className={cn('rise', ready && 'is-revealed')}
            style={{ '--d': '520ms' } as React.CSSProperties}
          >
            <Alert variant="success" className="mb-6 rounded-xl">
              <AlertDescription>
                If an account exists for <strong>{email}</strong>, we've sent a password reset link. Check your inbox.
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className={cn('rise flex flex-col gap-[14px]', ready && 'is-revealed')}
            style={{ '--d': '520ms' } as React.CSSProperties}
          >
            <div className="flex flex-col gap-[7px]">
              <Label htmlFor="email" className="text-[12.5px] tracking-[0.02em] text-[rgba(234,238,246,0.40)]">
                Work email
              </Label>
              <Input
                id="email"
                type="email"
                required
                autoFocus
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={FIELD_CLASS}
              />
            </div>
            <Button
              type="submit"
              disabled={loading || !email}
              className={cn('mt-[6px] h-12 w-full text-[15px] font-semibold', PRIMARY_BUTTON)}
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Send reset link'}
            </Button>
          </form>
        )}

        <div
          className={cn(
            'rise mt-6 flex items-center justify-center gap-3 text-[12px] text-[rgba(234,238,246,0.62)]',
            ready && 'is-revealed',
          )}
          style={{ '--d': '1000ms' } as React.CSSProperties}
        >
          <Link to="/login" className="hover:text-white">
            Back to sign in
          </Link>
          <span aria-hidden className="text-[rgba(234,238,246,0.30)]">·</span>
          <Link to="/signup" className="hover:text-white">
            14 day free trial
          </Link>
        </div>

        {/* Stratium provenance — quiet anchor line. */}
        <p
          className={cn(
            'rise mt-8 text-center text-[11px] tracking-wide text-[rgba(234,238,246,0.30)]',
            ready && 'is-revealed',
          )}
          style={{ '--d': '1200ms' } as React.CSSProperties}
        >
          Cadence is the product. Stratium is the company behind it.
        </p>
      </div>
    </div>
  );
}
